/**
 * LE SERVICE DU MODULE DES ALARMES V2 : LES LECTURES DES LOTS F1, F3, F4 ET F5,
 * LES ÉCRITURES DU LOT F6.
 * ============================================================================
 *
 * Neuf routes de la façade, toutes par l'alias `/v5` et jamais par le chemin
 * court : `ouverture` (la seule lecture de la garde serveur, toujours 200),
 * `offre` et `sms` (lot F5), `instance`, `occurrence` (la liste), `type`, le
 * détail d'une alarme `occurrence/:id` (lot F3), `membre` et la relecture des
 * messages de test d'un destinataire (lot F4). Lire ce détail CLÔT une alarme
 * revenue à la normale et non vue (D71) : seul le store du détail l'appelle,
 * et seule la page du détail appelle ce store, sur une navigation de la
 * personne (garde : `gardes.statiques.spec.ts`).
 *
 * Lot F6, cinq écritures, une seule requête chacune, jamais rejouée par le
 * service : modifier une surveillance (route 5), envoyer un message de test
 * (route 6), acquitter une alarme depuis l'écran (route 7), donner son avis
 * sur un écart (route 18), corriger le numéro d'un membre (route 8). Chacune
 * porte un délai de `DELAI_ECRITURE_MS` : au-delà, l'appel rend une erreur sans
 * statut, que l'écran lit « nous ne savons pas si c'est enregistré ».
 *
 * Les méthodes sont préfixées `alarmesV2` : le registre des services injecte
 * chaque méthode sous son nom, et un doublon y gagnerait en silence
 * (`core/plugins/services.plugin.ts`).
 */
import axios from "@core/plugins/axios.plugin"
import { appelAlarmesV2 } from "./appel"
import type {
    AlarmesV2Acquittement,
    AlarmesV2Avis,
    AlarmesV2Canal,
    AlarmesV2EnvoiTest,
    AlarmesV2Modification,
    AlarmesV2NumeroCorrige,
    AlarmesV2SurveillanceEcrite,
    AlarmesV2ValeurAvis,
    AlarmesV2Catalogue,
    AlarmesV2DetailAlarme,
    AlarmesV2FiltresAlarmes,
    AlarmesV2FiltresCatalogue,
    AlarmesV2PageMembres,
    AlarmesV2PageTests,
    AlarmesV2FiltresSurveillances,
    AlarmesV2Offre,
    AlarmesV2Ouverture,
    AlarmesV2PageAlarmes,
    AlarmesV2PageSurveillances,
    AlarmesV2Sms,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** La racine de la façade, par l'alias versionné. */
export const RACINE_ALARMES_V2 = "/api/metier/v5/alarmes-v2"

/**
 * Le délai d'une écriture : la façade attend le service 10 s, le routeur de
 * l'hébergeur coupe à 30 s. Au-delà, la réponse ne viendra plus.
 */
export const DELAI_ECRITURE_MS = 30000

/**
 * Les paramètres d'adresse, chacun une seule fois (la façade refuse un
 * paramètre répété) et sans valeur vide.
 */
function parametres(
    filtres: object
): Record<string, string | number | boolean> {
    const sortie: Record<string, string | number | boolean> = {}
    for (const [cle, valeur] of Object.entries(filtres)) {
        if (valeur === undefined || valeur === null || valeur === "") continue
        if (
            typeof valeur === "string" ||
            typeof valeur === "number" ||
            typeof valeur === "boolean"
        ) {
            sortie[cle] = valeur
        }
    }
    return sortie
}

export class AlarmesV2Service {
    /**
     * Le module est-il ouvert pour la session ? Toujours 200 `{ouvert}` (401
     * sans session), sans garde ni appel au service : la seule lecture de la
     * garde serveur, appelée par le seul `stores/ouverture.store.ts`.
     */
    async alarmesV2LireOuverture(): Promise<AlarmesV2Ouverture> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2Ouverture>(
                `${RACINE_ALARMES_V2}/ouverture`
            )
            return data
        })
    }

    /**
     * Ce que l'entreprise a (route 22) : niveau, essai, solde de SMS. Gardée
     * par `ALARMES_V2` (403 `alarmes_v2_fermee` pour un compte fermé) : elle
     * n'est appelée qu'après `/ouverture` à `true`, par le seul store de
     * l'ouverture.
     */
    async alarmesV2LireOffre(): Promise<AlarmesV2Offre> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2Offre>(
                `${RACINE_ALARMES_V2}/offre`
            )
            return data
        })
    }

    /**
     * Une page des surveillances de l'entreprise (routes 2 et 3).
     * @param filtres - filtres de la façade, chacun donné une fois
     */
    async alarmesV2ListerSurveillances(
        filtres: AlarmesV2FiltresSurveillances = {}
    ): Promise<AlarmesV2PageSurveillances> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2PageSurveillances>(
                `${RACINE_ALARMES_V2}/instance`,
                { params: parametres(filtres) }
            )
            return data
        })
    }

    /**
     * Une page des alarmes de l'entreprise (route E, la liste seulement).
     * @param filtres - filtres de la façade, chacun donné une fois
     */
    async alarmesV2ListerAlarmes(
        filtres: AlarmesV2FiltresAlarmes = {}
    ): Promise<AlarmesV2PageAlarmes> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2PageAlarmes>(
                `${RACINE_ALARMES_V2}/occurrence`,
                { params: parametres(filtres) }
            )
            return data
        })
    }

    /**
     * Le détail d'une alarme (route 12) : l'alarme, son journal et ses envois.
     * ⚠️ CETTE LECTURE A UN EFFET : elle clôt une alarme revenue à la normale
     * et non vue (D71). Jamais de préchargement, jamais au survol.
     * @param idAlarme - `_id` de l'alarme
     * @param limite - le nombre de lignes de journal, 500 au plus
     */
    async alarmesV2LireAlarme(
        idAlarme: string,
        limite = 500
    ): Promise<AlarmesV2DetailAlarme> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2DetailAlarme>(
                `${RACINE_ALARMES_V2}/occurrence/${encodeURIComponent(idAlarme)}`,
                { params: { limite } }
            )
            return data
        })
    }

    /**
     * Le solde de SMS de l'entreprise (route 15), relu par la page « Option
     * Avancé » ; `mouvements` n'y est servi qu'à un administrateur.
     */
    async alarmesV2LireSms(): Promise<AlarmesV2Sms> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2Sms>(
                `${RACINE_ALARMES_V2}/sms`
            )
            return data
        })
    }

    /** Le catalogue publié (route 1), sans `_id_objet` : pour le nom des types. */
    async alarmesV2ListerTypes(): Promise<AlarmesV2Catalogue> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2Catalogue>(
                `${RACINE_ALARMES_V2}/type`
            )
            return data
        })
    }

    /**
     * Le catalogue publié (route 1), filtré sur un équipement quand il est
     * désigné (lot F4, la page du catalogue). Sans `_id_objet`, les compteurs
     * de types écartés valent `null`.
     * @param filtres - l'équipement désigné, chaque paramètre donné une fois
     */
    async alarmesV2ListerCatalogue(
        filtres: AlarmesV2FiltresCatalogue = {}
    ): Promise<AlarmesV2Catalogue> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2Catalogue>(
                `${RACINE_ALARMES_V2}/type`,
                { params: parametres(filtres) }
            )
            return data
        })
    }

    /** Les membres de l'entreprise et l'état de leur numéro (route 9). */
    async alarmesV2ListerMembres(): Promise<AlarmesV2PageMembres> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2PageMembres>(
                `${RACINE_ALARMES_V2}/membre`
            )
            return data
        })
    }

    /**
     * Les derniers messages de test d'un destinataire (route 6 bis), statuts
     * seulement. Lue au clic de la personne, une fois par clic.
     * @param idInstance - la surveillance
     * @param idUser - le destinataire
     * @param limite - le nombre de lignes, 1 à 50
     */
    async alarmesV2LireTests(
        idInstance: string,
        idUser: string,
        limite: number
    ): Promise<AlarmesV2PageTests> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.get<AlarmesV2PageTests>(
                `${RACINE_ALARMES_V2}/instance/${encodeURIComponent(idInstance)}/destinataire/${encodeURIComponent(idUser)}/test`,
                { params: { limite } }
            )
            return data
        })
    }

    // ------------------------------------------------------- écritures (F6)

    /**
     * Modifie une surveillance (route 5) : seuils, destinataires, consigne,
     * désactivation ou réactivation. Le corps ne porte que les clés posées ;
     * l'appelant n'envoie jamais un corps vide.
     * @param idInstance - la surveillance
     * @param corps - les champs modifiés
     */
    async alarmesV2ModifierSurveillance(
        idInstance: string,
        corps: AlarmesV2Modification
    ): Promise<AlarmesV2SurveillanceEcrite> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.put<AlarmesV2SurveillanceEcrite>(
                `${RACINE_ALARMES_V2}/instance/${encodeURIComponent(idInstance)}`,
                corps,
                { timeout: DELAI_ECRITURE_MS }
            )
            return data
        })
    }

    /**
     * Envoie un message de test à un destinataire d'une surveillance (route
     * 6) : un vrai message, par le canal choisi.
     * @param idInstance - la surveillance
     * @param idUser - le destinataire
     * @param canal - `sms` ou `mail`
     */
    async alarmesV2EnvoyerTest(
        idInstance: string,
        idUser: string,
        canal: AlarmesV2Canal
    ): Promise<AlarmesV2EnvoiTest> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.post<AlarmesV2EnvoiTest>(
                `${RACINE_ALARMES_V2}/instance/${encodeURIComponent(idInstance)}/destinataire/${encodeURIComponent(idUser)}/test`,
                { canal },
                { timeout: DELAI_ECRITURE_MS }
            )
            return data
        })
    }

    /**
     * Acquitte une alarme depuis l'écran (route 7), corps vide. Un second
     * appui rend 200 avec le PREMIER acquitteur.
     * @param idAlarme - `_id` de l'alarme
     */
    async alarmesV2Acquitter(idAlarme: string): Promise<AlarmesV2Acquittement> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.post<AlarmesV2Acquittement>(
                `${RACINE_ALARMES_V2}/occurrence/${encodeURIComponent(idAlarme)}/prise-en-compte`,
                {},
                { timeout: DELAI_ECRITURE_MS }
            )
            return data
        })
    }

    /**
     * « Cette alerte était-elle utile ? » sur un écart (route 18).
     * @param idAlarme - `_id` de l'alarme
     * @param valeur - `utile` ou `pas_utile`
     */
    async alarmesV2DonnerAvis(
        idAlarme: string,
        valeur: AlarmesV2ValeurAvis
    ): Promise<AlarmesV2Avis> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.post<AlarmesV2Avis>(
                `${RACINE_ALARMES_V2}/occurrence/${encodeURIComponent(idAlarme)}/avis`,
                { valeur },
                { timeout: DELAI_ECRITURE_MS }
            )
            return data
        })
    }

    /**
     * Corrige le numéro d'un membre (route 8). Le numéro saisi part dans le
     * corps et n'est gardé nulle part ; la réponse n'est rendue que sans
     * `phone` (seuls l'état et le masque sont lus).
     * @param idUser - le membre
     * @param phone - le numéro tel que saisi
     */
    async alarmesV2CorrigerNumero(
        idUser: string,
        phone: string
    ): Promise<AlarmesV2NumeroCorrige> {
        return appelAlarmesV2(async () => {
            const { data } = await axios.put<
                AlarmesV2NumeroCorrige & { phone?: unknown }
            >(
                `${RACINE_ALARMES_V2}/user/${encodeURIComponent(idUser)}/phone`,
                { phone },
                { timeout: DELAI_ECRITURE_MS }
            )
            const { phone: _jamaisLu, ...sansNumero } = data ?? {
                _id: idUser,
            }
            return sansNumero
        })
    }
}

/** L'instance partagée par les stores du module. */
export const serviceAlarmesV2 = new AlarmesV2Service()
